import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Sparkles, X } from "lucide-react";

import { RELEASE_ANNOUNCEMENT } from "@/const/release";
import { isFeatureEnabled } from "../../services/flagsService";
import { cn } from "@/lib/utils";

const DISMISSED_KEY = "release_banner_dismissed";

// Full-screen tools where a floating banner would get in the way.
const HIDDEN_PREFIXES = ["/timer", "/buzzer", "/games/", "/sheets", "/scoring"];

const readDismissed = (id: string) => {
  try {
    return localStorage.getItem(DISMISSED_KEY) === id;
  } catch {
    return false;
  }
};

const markDismissed = (id: string) => {
  try {
    localStorage.setItem(DISMISSED_KEY, id);
  } catch {
    // ignore
  }
};

const isExpired = (until: string) => {
  const end = Date.parse(until);
  return Number.isNaN(end) || Date.now() > end;
};

/**
 * Small bottom-left card announcing the latest release (see const/release.ts).
 * Shown until its `until` deadline, unless closed or its link is followed
 * (remembered per release id).
 */
const ReleaseBanner: React.FC = () => {
  const { pathname } = useLocation();
  const release = RELEASE_ANNOUNCEMENT;
  const [dismissed, setDismissed] = useState(() => !release || readDismissed(release.id));
  const hidden = HIDDEN_PREFIXES.some((p) => pathname === p || pathname.startsWith(p.endsWith("/") ? p : p + "/"));

  if (!release || !release.enabled || isExpired(release.until) || dismissed || hidden || !isFeatureEnabled("RELEASE_BANNER")) return null;

  const dismiss = () => {
    setDismissed(true);
    markDismissed(release.id);
  };

  const external = release.link && /^https?:\/\//.test(release.link);
  const linkClass =
    "inline-flex items-center gap-1 text-xs font-semibold text-blue-600 dark:text-indigo-300 hover:text-blue-700 dark:hover:text-indigo-200 hover:underline";
  const linkContent = (
    <>
      {release.linkLabel ?? "Ətraflı"}
      <ArrowRight className="w-3.5 h-3.5" />
    </>
  );

  return (
    // right-20 on mobile keeps clear of the feature request button in the bottom-right corner
    <div
      role="status"
      className={cn(
        "fixed bottom-4 left-4 right-20 sm:right-auto sm:max-w-sm z-40 print:hidden",
        "flex items-start gap-3 rounded-2xl border border-blue-100 dark:border-indigo-900",
        "bg-white/95 dark:bg-gray-900/95 backdrop-blur-md px-3.5 py-3 shadow-lg",
        "animate-in fade-in slide-in-from-bottom-2 duration-300"
      )}
    >
      <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 text-white">
        <Sparkles className="w-4 h-4" />
      </span>

      <div className="min-w-0 flex-1 pr-4">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-500 dark:text-indigo-400">Yenilik</p>
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{release.title}</p>
        {release.description && (
          <p className="mt-0.5 text-xs leading-snug text-gray-500 dark:text-gray-400">{release.description}</p>
        )}
        {release.link && (
          <div className="mt-1.5">
            {external ? (
              <a href={release.link} target="_blank" rel="noopener noreferrer" onClick={dismiss} className={linkClass}>
                {linkContent}
              </a>
            ) : (
              <Link to={release.link} onClick={dismiss} className={linkClass}>
                {linkContent}
              </Link>
            )}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={dismiss}
        aria-label="Bağla"
        className="appearance-none absolute top-2 right-2 p-0.5 text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default ReleaseBanner;
